export type DeviceType = 'mobile' | 'tablet' | 'desktop' | 'unknown'
export type DeviceOrientation = 'landscape' | 'portrait' | 'unknown'
export type DeviceOs =
  | 'ios'
  | 'iphone'
  | 'ipad'
  | 'ipod'
  | 'android'
  | 'blackberry'
  | 'macos'
  | 'windows'
  | 'fxos'
  | 'meego'
  | 'television'
  | 'unknown'

export type OrientationChangeCallback = (newOrientation: 'landscape' | 'portrait') => void

export interface Device {
  macos(): boolean
  ios(): boolean
  iphone(): boolean
  ipod(): boolean
  ipad(): boolean
  android(): boolean
  androidPhone(): boolean
  androidTablet(): boolean
  blackberry(): boolean
  blackberryPhone(): boolean
  blackberryTablet(): boolean
  windows(): boolean
  windowsPhone(): boolean
  windowsTablet(): boolean
  fxos(): boolean
  fxosPhone(): boolean
  fxosTablet(): boolean
  meego(): boolean
  television(): boolean
  cordova(): boolean
  nodeWebkit(): boolean
  mobile(): boolean
  tablet(): boolean
  desktop(): boolean
  portrait(): boolean
  landscape(): boolean
  onChangeOrientation(cb: OrientationChangeCallback): void
  noConflict(): Device
  type: DeviceType
  os: DeviceOs
  orientation: DeviceOrientation
}

declare global {
  interface Window {
    device: Device
  }
}

// Save the previous value of the device variable.
const previousDevice = window.device

const device = {} as Device

const changeOrientationList: OrientationChangeCallback[] = []

// Add device as a global object.
window.device = device

// The <html> element.
const documentElement = window.document.documentElement

// The client user agent string.
// Lowercase, so we can use the more efficient indexOf(), instead of Regex
const userAgent = window.navigator.userAgent.toLowerCase()

// Detectable television devices.
const televisionDevices: string[] = [
  'googletv',
  'viera',
  'smarttv',
  'internet.tv',
  'netcast',
  'nettv',
  'appletv',
  'boxee',
  'kylo',
  'roku',
  'dlnadoc',
  'pov_tv',
  'hbbtv',
  'ce-html'
]

// Private Utility Functions
// -------------------------

// Check if element exists
function includes(haystack: string, needle: string): boolean {
  return haystack.indexOf(needle) !== -1
}

// Simple UA string search
function find(needle: string): boolean {
  return includes(userAgent, needle)
}

// Check if documentElement already has a given class.
function hasClass(className: string): RegExpMatchArray | null {
  return documentElement.className.match(new RegExp(className, 'i'))
}

// Add one or more CSS classes to the <html> element.
function addClass(className: string): void {
  let currentClassNames: string | null = null
  if (!hasClass(className)) {
    currentClassNames = documentElement.className.replace(/^\s+|\s+$/g, '')
    documentElement.className = `${currentClassNames} ${className}`
  }
}

// Remove single CSS class from the <html> element.
function removeClass(className: string): void {
  if (hasClass(className)) {
    documentElement.className = documentElement.className.replace(
      ` ${className}`,
      ''
    )
  }
}

// Main functions
// --------------

device.macos = function (): boolean {
  return find('mac')
}

device.ios = function (): boolean {
  return device.iphone() || device.ipod() || device.ipad()
}

device.iphone = function (): boolean {
  return !device.windows() && find('iphone')
}

device.ipod = function (): boolean {
  return find('ipod')
}

device.ipad = function (): boolean {
  const iPadOS13Up =
    navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1
  return find('ipad') || iPadOS13Up
}

device.android = function (): boolean {
  return !device.windows() && find('android')
}

device.androidPhone = function (): boolean {
  return device.android() && find('mobile')
}

device.androidTablet = function (): boolean {
  return device.android() && !find('mobile')
}

device.blackberry = function (): boolean {
  return find('blackberry') || find('bb10')
}

device.blackberryPhone = function (): boolean {
  return device.blackberry() && !find('tablet')
}

device.blackberryTablet = function (): boolean {
  return device.blackberry() && find('tablet')
}

device.windows = function (): boolean {
  return find('windows')
}

device.windowsPhone = function (): boolean {
  return device.windows() && find('phone')
}

device.windowsTablet = function (): boolean {
  return device.windows() && (find('touch') && !device.windowsPhone())
}

device.fxos = function (): boolean {
  return (find('(mobile') || find('(tablet')) && find(' rv:')
}

device.fxosPhone = function (): boolean {
  return device.fxos() && find('mobile')
}

device.fxosTablet = function (): boolean {
  return device.fxos() && find('tablet')
}

device.meego = function (): boolean {
  return find('meego')
}

device.cordova = function (): boolean {
  return !!(window as Window & { cordova?: unknown }).cordova && location.protocol === 'file:'
}

device.nodeWebkit = function (): boolean {
  return typeof (window as Window & { process?: unknown }).process === 'object'
}

device.mobile = function (): boolean {
  return (
    device.androidPhone() ||
    device.iphone() ||
    device.ipod() ||
    device.windowsPhone() ||
    device.blackberryPhone() ||
    device.fxosPhone() ||
    device.meego()
  )
}

device.tablet = function (): boolean {
  return (
    device.ipad() ||
    device.androidTablet() ||
    device.blackberryTablet() ||
    device.windowsTablet() ||
    device.fxosTablet()
  )
}

device.desktop = function (): boolean {
  return !device.tablet() && !device.mobile()
}

device.television = function (): boolean {
  let i = 0
  while (i < televisionDevices.length) {
    if (find(televisionDevices[i])) {
      return true
    }
    i++
  }
  return false
}

device.portrait = function (): boolean {
  if (
    screen.orientation &&
    Object.prototype.hasOwnProperty.call(window, 'onorientationchange')
  ) {
    return includes(screen.orientation.type, 'portrait')
  }
  if (
    device.ios() &&
    Object.prototype.hasOwnProperty.call(window, 'orientation')
  ) {
    return Math.abs(window.orientation as number) !== 90
  }
  return window.innerHeight / window.innerWidth > 1
}

device.landscape = function (): boolean {
  if (
    screen.orientation &&
    Object.prototype.hasOwnProperty.call(window, 'onorientationchange')
  ) {
    return includes(screen.orientation.type, 'landscape')
  }
  if (
    device.ios() &&
    Object.prototype.hasOwnProperty.call(window, 'orientation')
  ) {
    return Math.abs(window.orientation as number) === 90
  }
  return window.innerHeight / window.innerWidth < 1
}

// Public Utility Functions
// ------------------------

// Run device.js in noConflict mode,
// returning the device variable to its previous owner.
device.noConflict = function (): Device {
  window.device = previousDevice
  return this
}

// HTML Element Handling
// ---------------------

// Insert the appropriate CSS class based on the user agent.

if (device.ios()) {
  if (device.ipad()) {
    addClass('ios ipad tablet')
  } else if (device.iphone()) {
    addClass('ios iphone mobile')
  } else if (device.ipod()) {
    addClass('ios ipod mobile')
  }
} else if (device.macos()) {
  addClass('macos desktop')
} else if (device.android()) {
  if (device.androidTablet()) {
    addClass('android tablet')
  } else {
    addClass('android mobile')
  }
} else if (device.blackberry()) {
  if (device.blackberryTablet()) {
    addClass('blackberry tablet')
  } else {
    addClass('blackberry mobile')
  }
} else if (device.windows()) {
  if (device.windowsTablet()) {
    addClass('windows tablet')
  } else if (device.windowsPhone()) {
    addClass('windows mobile')
  } else {
    addClass('windows desktop')
  }
} else if (device.fxos()) {
  if (device.fxosTablet()) {
    addClass('fxos tablet')
  } else {
    addClass('fxos mobile')
  }
} else if (device.meego()) {
  addClass('meego mobile')
} else if (device.nodeWebkit()) {
  addClass('node-webkit')
} else if (device.television()) {
  addClass('television')
} else if (device.desktop()) {
  addClass('desktop')
}

if (device.cordova()) {
  addClass('cordova')
}

// Orientation Handling
// --------------------

// Handle device orientation changes.
function handleOrientation(): void {
  if (device.landscape()) {
    removeClass('portrait')
    addClass('landscape')
    walkOnChangeOrientationList('landscape')
  } else {
    removeClass('landscape')
    addClass('portrait')
    walkOnChangeOrientationList('portrait')
  }
  setOrientationCache()
}

function walkOnChangeOrientationList(newOrientation: 'landscape' | 'portrait'): void {
  for (let index = 0; index < changeOrientationList.length; index++) {
    changeOrientationList[index](newOrientation)
  }
}

device.onChangeOrientation = function (cb: OrientationChangeCallback): void {
  if (typeof cb === 'function') {
    changeOrientationList.push(cb)
  }
}

// Detect whether device supports orientationchange event,
// otherwise fall back to the resize event.
let orientationEvent = 'resize'
if (Object.prototype.hasOwnProperty.call(window, 'onorientationchange')) {
  orientationEvent = 'orientationchange'
}

// Listen for changes in orientation.
window.addEventListener(orientationEvent, handleOrientation, false)

handleOrientation()

// Public functions to get the current value of type, os, or orientation
// ---------------------------------------------------------------------

function findMatch<T extends string>(arr: T[]): T | 'unknown' {
  for (let i = 0; i < arr.length; i++) {
    if (device[arr[i] as keyof Device] && typeof device[arr[i] as keyof Device] === 'function' && (device[arr[i] as keyof Device] as () => boolean)()) {
      return arr[i]
    }
  }
  return 'unknown'
}

device.type = findMatch(['mobile', 'tablet', 'desktop']) as DeviceType
device.os = findMatch([
  'ios',
  'iphone',
  'ipad',
  'ipod',
  'android',
  'blackberry',
  'macos',
  'windows',
  'fxos',
  'meego',
  'television'
]) as DeviceOs

function setOrientationCache(): void {
  device.orientation = findMatch(['portrait', 'landscape']) as DeviceOrientation
}

setOrientationCache()

export default device
